import { Link } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import type { CatalogueTechnology, PqcStatus } from '@/api/types/catalogue'
import { monogram, monthLabel, STATUS_LABEL, STATUS_TOKEN } from '@/lib/catalogue/engine'
import { dependencySummary } from '@/lib/catalogue/summary'
import type { ModuleCtx } from '@/lib/readiness/module'
import { useDiscoveryDependencies } from '@/api/clients/discoveryApi'
import type * as discovery from '@/api/types/discovery'
import { SectionLabel } from '@/components/readiness/primitives'

// ── Catalogue UI primitives (I3 §9–§11) ─────────────────────────────────────

// Mark: the logo asset when present (monochrome SVG tinted to currentColor),
// else a deterministic monogram (Note 8), 32px, 8px radius.
export function TechMark({ tech, size = 32 }: { tech: Pick<CatalogueTechnology, 'vendor' | 'logo_asset' | 'name'>; size?: number }) {
  const { theme } = useTheme()
  if (tech.logo_asset) {
    return <span className="grid shrink-0 place-items-center rounded-[8px]" style={{ width: size, height: size, background: 'var(--rd-bg-tile)', color: 'var(--rd-text-primary)' }} aria-hidden="true" dangerouslySetInnerHTML={{ __html: tech.logo_asset }} />
  }
  const m = monogram(tech.vendor, theme === 'light' ? 'light' : 'dark')
  return (
    <span className="rd-mono grid shrink-0 place-items-center rounded-[8px] font-semibold" style={{ width: size, height: size, background: m.bg, color: m.fg, fontSize: size <= 24 ? 9 : 11 }} aria-hidden="true">
      {m.letters}
    </span>
  )
}

// Roll-up chip (§10.1): PQC-ready green, Partial blue, Not PQC · roadmap amber,
// Not PQC · no roadmap grey, Unknown dashed.
export function PqcChip({ status, date, upper = false, size }: { status: PqcStatus; date?: string | null; upper?: boolean; size?: 'lg' }) {
  const base = { pqc_ready: { background: 'var(--rd-status-ok-bg)', color: 'var(--rd-status-ok-text)' }, partial: { background: 'var(--rd-accent-chip-bg)', color: 'var(--rd-status-info)' }, roadmap: { background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' }, none: { background: 'var(--rd-bg-tile)', color: 'var(--rd-text-muted)' }, unknown: { background: 'transparent', color: 'var(--rd-text-muted)', border: '1px dashed var(--rd-border-control)' } }[status]
  const text = status === 'roadmap' && date ? `Not PQC · roadmap ${monthLabel(date)}` : STATUS_LABEL[status]
  return <span className={`rd-chip whitespace-nowrap ${size === 'lg' ? 'rd-chip-lg' : ''}`} style={{ ...base, letterSpacing: upper ? '0.06em' : undefined }}>{upper ? text.toUpperCase() : text}</span>
}

type StatusSegment = 'pqc_ready' | 'partial' | 'roadmap' | 'other'

// Four-segment PQC status bar over technologies in use. `labels` renames
// segments whose meaning differs, e.g. Discovery's not ready in the amber one.
export function StatusBar({ counts, height = 10, labels }: { counts: Record<StatusSegment, number>; height?: number; labels?: Partial<Record<StatusSegment, string>> }) {
  const total = counts.pqc_ready + counts.partial + counts.roadmap + counts.other
  const segs: Array<[StatusSegment, string, string]> = (
    [['pqc_ready', STATUS_TOKEN.pqc_ready, 'PQC-ready'], ['partial', STATUS_TOKEN.partial, 'partial'], ['roadmap', STATUS_TOKEN.roadmap, 'roadmap'], ['other', 'var(--rd-status-neutral)', 'none / unknown']] as Array<[StatusSegment, string, string]>
  ).map(([k, color, l]) => [k, color, labels?.[k] ?? l])
  return (
    <div>
      <div className="flex w-full overflow-hidden rounded-full" style={{ height, gap: 2, background: total ? undefined : 'var(--rd-bg-tile)' }} role="img" aria-label={segs.map(([k, , l]) => `${l} ${counts[k]}`).join(', ')}>
        {segs.map(([k, color, l]) => (counts[k] > 0 ? <div key={k} style={{ flex: counts[k], background: color }} title={`${l} · ${counts[k]}`} /> : null))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
        {segs.map(([k, color, l]) => (
          <li key={k} className="flex items-center gap-1 whitespace-nowrap"><span className="inline-block rounded-sm" style={{ width: 9, height: 9, background: color }} /> {l} <span className="rd-mono">{counts[k]}</span></li>
        ))}
      </ul>
    </div>
  )
}

// Overview fourth summary tile (I3 §2.1).
// `hideLink`: POC drops the `Catalogue →` link and keeps the line's text (I6 POC §6.2).
// A real organisation's tile reads Discovery's vendor components instead of
// the catalogue (DiscoveryDependencies); with no organisation it shows the
// catalogue's.
export function TechDependenciesCard({ m, className = '', hideLink = false }: { m: ModuleCtx; className?: string; hideLink?: boolean }) {
  const deps = useDiscoveryDependencies(m.orgId)
  const s = dependencySummary(m)
  const q = `?index=${m.indexId}`
  if (deps.enabled) return <DiscoveryDependencies deps={deps} className={className} />
  return (
    <section className={`rd-card p-5 ${className}`} aria-labelledby="rd-deps-label" data-testid="tech-dependencies-card">
      <SectionLabel><span id="rd-deps-label">Technology dependencies</span></SectionLabel>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="rd-kpi" data-testid="deps-in-use">{s.inUse.length}</span>
        <span className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>external technologies in use · {s.vendors} vendors</span>
      </div>
      <div className="mt-3"><StatusBar counts={s.counts} /></div>
      <p className="mt-3 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }} data-testid="deps-gaps">
        <span style={{ color: s.gatedItems ? 'var(--rd-status-warn)' : undefined }}>{s.gatedItems} vendor-gated {s.gatedItems === 1 ? 'gap' : 'gaps'}</span>
        {s.earliest && <> · earliest closes {monthLabel(s.earliest.roadmap_date)} ({s.earliest.technology.vendor})</>}
        {s.latest && s.latest !== s.earliest && <> · latest {monthLabel(s.latest.roadmap_date)} ({s.latest.technology.vendor})</>}
      </p>
      <p className="mt-4 flex items-center justify-between gap-2 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
        <span data-testid="deps-unmatched">{s.unmatched.length} detected, not yet in catalogue</span>
        {!hideLink && <Link to={`/readiness/catalogue${q}`} className="rd-link shrink-0">Catalogue →</Link>}
      </p>
    </section>
  )
}

const byDate = (key: 'earliest_deadline' | 'latest_deadline') => (a: discovery.Dependency, b: discovery.Dependency) => (a[key] ?? '').localeCompare(b[key] ?? '')

// The tile from Discovery (GET /discovery/dependencies): the vendor components
// it found, each PQC-ready, partial or not ready by its assets' quantum risk;
// the gaps are those with a vulnerable asset left, dated by the remediation
// deadlines Discovery set on their assets.
function DiscoveryDependencies({ deps, className }: { deps: ReturnType<typeof useDiscoveryDependencies>; className: string }) {
  const rep = deps.data ?? null
  const items = rep?.items ?? []
  const gaps = items.filter((d) => d.gap)
  const earliest = gaps.filter((d) => d.earliest_deadline).sort(byDate('earliest_deadline'))[0] ?? null
  const latest = gaps.filter((d) => d.latest_deadline).sort(byDate('latest_deadline')).pop() ?? null
  const systems = new Set(items.flatMap((d) => d.system_ids ?? [])).size
  // Optional chaining throughout: a stubbed or older backend may answer without the fields.
  const counts = { pqc_ready: rep?.status?.pqc_ready ?? 0, partial: rep?.status?.partial ?? 0, roadmap: rep?.status?.not_ready ?? 0, other: rep?.status?.unknown ?? 0 }
  const undated = rep?.undated_vulnerable_count ?? 0
  return (
    <section className={`rd-card p-5 ${className}`} aria-labelledby="rd-deps-label" data-testid="tech-dependencies-card">
      <SectionLabel><span id="rd-deps-label">Technology dependencies</span></SectionLabel>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="rd-kpi" data-testid="deps-in-use">{rep ? items.length : '—'}</span>
        <span className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
          {deps.isPending ? 'Loading vendor components from Discovery…' : deps.isError ? "Couldn't load vendor components from Discovery" : `vendor ${items.length === 1 ? 'component' : 'components'} found by Discovery · ${systems} ${systems === 1 ? 'system' : 'systems'}`}
        </span>
      </div>
      <div className="mt-3"><StatusBar counts={counts} labels={{ roadmap: 'not ready', other: 'unknown' }} /></div>
      <p className="mt-3 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }} data-testid="deps-gaps">
        <span style={{ color: gaps.length ? 'var(--rd-status-warn)' : undefined }}>{gaps.length} vendor-gated {gaps.length === 1 ? 'gap' : 'gaps'}</span>
        {earliest && <> · earliest closes {monthLabel(earliest.earliest_deadline)} ({earliest.name})</>}
        {latest && latest.latest_deadline !== earliest?.earliest_deadline && <> · latest {monthLabel(latest.latest_deadline)} ({latest.name})</>}
      </p>
      <p className="mt-4 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="deps-unmatched">
        {undated} vulnerable {undated === 1 ? 'asset' : 'assets'} without a remediation deadline
      </p>
    </section>
  )
}
